/**
 * Heuristic content-format scanner.
 *
 * Nostr is permissionless — a NIP-54 wiki article (`kind:30818`) or an
 * NKBIP-01 publication section (`kind:30041`) can, in practice, contain
 * Djot (the NIP-54-mandated format), AsciiDoc (what NKBIP-01 itself uses,
 * and what many wiki-migration tools produce), plain CommonMark-ish
 * Markdown (extremely common regardless of what the spec says), or just
 * plain prose with no markup at all. Wikistr renders whichever one it
 * actually is, rather than assuming Djot and mangling everything else.
 *
 * This is a best-effort heuristic, not a formal grammar disambiguator —
 * Djot was deliberately designed to look almost exactly like Markdown
 * (both use `#` ATX headings, `[text](url)` links, `` `code` `` spans,
 * `- item` lists, and fenced ``` code blocks), so a handful of documents
 * are genuinely ambiguous between the two. When automatic detection is
 * uncertain, the UI (see `ContentFormatSelect` + `useContentFormat`) lets
 * the reader manually pick the format, or view the raw, unformatted
 * source.
 *
 * Detection strategy, in order of confidence:
 *
 * 1. AsciiDoc has several block-level constructs with no Markdown/Djot
 *    equivalent at all: `=`-prefixed headings (vs. `#` for the other
 *    two), `NOTE:`/`TIP:`/`IMPORTANT:`/`WARNING:`/`CAUTION:` admonitions,
 *    `----`/`....`/`____`/`|===` delimited blocks, `[block,attrs]` lines,
 *    `:doc-attribute:` entries, and `image::` block macros. Any of these
 *    is decisive.
 * 2. Djot has its own inline/block constructs with no Markdown
 *    equivalent: `:::` div fences, `{+insert+}` / `{-delete-}` /
 *    `{=highlight=}` spans, `` $`inline math` ``, `[^footnote]`
 *    references. (Note: single-delimiter `*strong*` is NOT a reliable
 *    Djot signal on its own — AsciiDoc uses the identical convention
 *    for bold, so treating it as Djot-only misclassified plain AsciiDoc
 *    prose as Djot and, worse, sometimes crashed the Djot parser on
 *    AsciiDoc-only constructs it doesn't understand, silently rendering
 *    a blank document. See the safety net in `WikiContent`.)
 * 3. Markdown has doubled emphasis delimiters (`**strong**` /
 *    `__strong__`) that Djot doesn't use, and setext headings
 *    (`Text\n=====` / `Text\n-----`) that neither Djot nor AsciiDoc use.
 * 4. If none of the above appear but the text still has *some* structure
 *    (an ATX `#` heading, a `[text](url)` link, a bullet list), it's
 *    ambiguously Djot-or-Markdown; default to Djot since that's what
 *    NIP-54 mandates.
 * 5. If nothing matches at all, it's plaintext.
 */

export type ContentFormat = 'djot' | 'asciidoc' | 'markdown' | 'plaintext';

export const CONTENT_FORMATS: ContentFormat[] = ['djot', 'asciidoc', 'markdown', 'plaintext'];

export const CONTENT_FORMAT_LABELS: Record<ContentFormat, string> = {
  djot: 'Djot',
  asciidoc: 'AsciiDoc',
  markdown: 'Markdown',
  plaintext: 'Plain text',
};

interface FormatSignal {
  re: RegExp;
  weight: number;
}

// --- AsciiDoc-only signals -------------------------------------------------
const ASCIIDOC_SIGNALS: FormatSignal[] = [
  { re: /^={1,6}\s+\S/m, weight: 4 },
  { re: /^(NOTE|TIP|IMPORTANT|WARNING|CAUTION):\s+\S/m, weight: 4 },
  { re: /^-{4,}\s*$/m, weight: 4 },
  { re: /^\.{4,}\s*$/m, weight: 4 },
  { re: /^_{4,}\s*$/m, weight: 4 },
  { re: /^\|=+\s*$/m, weight: 4 },
  { re: /^\[(source|quote|NOTE|TIP|IMPORTANT|WARNING|CAUTION)[,\]]/im, weight: 3 },
  { re: /^:[^:\s][^:]*:(\s.*)?$/m, weight: 2 },
  { re: /^image::\S/m, weight: 3 },
];

// --- Djot-only signals -------------------------------------------------
// Deliberately does NOT include single-delimiter `*strong*` as a signal:
// AsciiDoc uses the exact same convention for bold, so it's not
// Djot-exclusive and using it as one caused AsciiDoc prose (especially
// short documents with no headings/admonitions) to be misdetected as
// Djot.
const DJOT_SIGNALS: FormatSignal[] = [
  { re: /^:{3,}\s*$/m, weight: 4 },
  { re: /\{[+=-][^{}\n]+[+=-]\}/, weight: 3 },
  { re: /\$`[^`\n]+`/, weight: 3 },
  { re: /\[\^[^\]\n]+\]/, weight: 2 },
];

// --- Markdown-only signals -------------------------------------------------
const MARKDOWN_SIGNALS: FormatSignal[] = [
  { re: /\*\*[^\s*][^*\n]*?\*\*/, weight: 3 },
  { re: /__[^\s_][^_\n]*?__/, weight: 3 },
  // Setext heading: a non-blank text line immediately followed by a line
  // of only `=` or `-` characters (length >= 1), with no leading `=`/`-`
  // on the text line itself (which would just be an AsciiDoc heading or
  // thematic break).
  { re: /^[^\s=\-#][^\n]*\n(?:=+|-{2,})\s*$/m, weight: 3 },
];

// --- Weak signals shared by both Djot and Markdown -------------------------
const WEAK_LIGHTWEIGHT_MARKUP_SIGNALS: FormatSignal[] = [
  { re: /^#{1,6}\s+\S/m, weight: 1 },
  { re: /\[[^\]\n]+\]\([^)\n]+\)/, weight: 1 },
  { re: /^ {0,3}[-*+]\s+\S/m, weight: 1 },
  { re: /^ {0,3}\d+\.\s+\S/m, weight: 1 },
  { re: /^```/m, weight: 1 },
  { re: /^>\s?\S/m, weight: 1 },
];

function score(content: string, signals: FormatSignal[]): number {
  let total = 0;
  for (const { re, weight } of signals) {
    if (re.test(content)) total += weight;
  }
  return total;
}

/**
 * Scans `content` and returns the most likely markup format. Never
 * throws — malformed regex input (there is none, since these are fixed
 * patterns) or empty content simply falls through to `'plaintext'`.
 */
/**
 * Narrows an untrusted string (a stored draft's remembered format, a
 * URL param) to a `ContentFormat`. Anything unrecognized is rejected so
 * the caller can fall back to detection rather than passing a bogus
 * value into a renderer.
 */
export function isContentFormat(value: unknown): value is ContentFormat {
  return typeof value === 'string' && (CONTENT_FORMATS as string[]).includes(value);
}

export function detectContentFormat(content: string): ContentFormat {
  const trimmed = content.trim();
  if (!trimmed) return 'plaintext';

  const asciidocScore = score(trimmed, ASCIIDOC_SIGNALS);
  const djotScore = score(trimmed, DJOT_SIGNALS);
  const markdownScore = score(trimmed, MARKDOWN_SIGNALS);

  if (asciidocScore > 0 && asciidocScore >= djotScore && asciidocScore >= markdownScore) {
    return 'asciidoc';
  }
  if (djotScore > 0 && djotScore >= markdownScore) {
    return 'djot';
  }
  if (markdownScore > 0) {
    return 'markdown';
  }

  // No decisive signals either way — fall back to whether there's *any*
  // lightweight markup at all. If so, prefer Djot (the NIP-54-mandated
  // format for wiki articles); otherwise it's just prose.
  const weakScore = score(trimmed, WEAK_LIGHTWEIGHT_MARKUP_SIGNALS);
  return weakScore > 0 ? 'djot' : 'plaintext';
}
